import React, { useState, useEffect, createContext, useContext } from 'react';
import Keycloak from 'keycloak-js';
import { setKeycloakInstance, resolveKeycloakReady } from '../services/api';

// Define Keycloak configuration
// In development (npm start), use REACT_APP_KEYCLOAK_URL if provided
// In production, use same-domain /auth
const keycloakConfig = {
  url: process.env.REACT_APP_KEYCLOAK_URL ||
       (typeof window !== 'undefined' ? window.location.origin + '/auth' : '/auth'),
  realm: process.env.REACT_APP_KEYCLOAK_REALM || 'oplus',
  clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'oplus',
};

// Module-level singletons. Promise-based init so React 18 StrictMode's double
// effect can't race past an unset api.js singleton (the bug that surfaced when
// the license gate was removed from ui-admin lives in this file too).
const keycloak = new Keycloak(keycloakConfig);
let initPromise = null;

const LoadingComponent = () => (
  <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center">
    <img src={`${process.env.PUBLIC_URL || ''}/operator_plus_logo_hd.png`} alt="Operator+" className="w-16 h-16 animate-pulse-logo mb-4 object-contain" />
    <p className="text-slate-600 text-sm font-medium">Loading Keycloak...</p>
  </div>
);

// Create context for Keycloak
const KeycloakContext = createContext(null);

export const useKeycloak = () => {
  return useContext(KeycloakContext);
};

function getInitPromise() {
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      // Check if we're in a secure context (HTTPS or localhost)
      const isSecureContext = window.isSecureContext || window.location.protocol === 'https:' ||
                              window.location.hostname === 'localhost' ||
                              window.location.hostname === '127.0.0.1';

      const initOptions = {
        onLoad: 'login-required',
        checkLoginIframe: false,
        enableLogging: true,
        redirectUri: window.location.origin + window.location.pathname,
      };

      // Only use PKCE in secure contexts where Web Crypto API is available
      if (isSecureContext && window.crypto && window.crypto.subtle) {
        initOptions.pkceMethod = 'S256';
      } else {
        console.warn('Running in non-secure context. PKCE will be disabled.');
        initOptions.flow = 'standard';
      }

      const isAuth = await keycloak.init(initOptions);

      // Expose the singleton to api.js *before* any children can mount and
      // start firing requests.
      setKeycloakInstance(keycloak);
      resolveKeycloakReady(keycloak);

      // Proactive recovery: when the access token expires, try a silent
      // refresh; if no refresh token is available (PKCE without offline_access)
      // redirect to KC's auth endpoint — which silently re-auths via the SSO
      // cookie when it's still valid, no full logout dance required.
      keycloak.onTokenExpired = () => {
        keycloak.updateToken(-1).catch(() => {
          keycloak.login({ redirectUri: window.location.href });
        });
      };

      console.log('Keycloak initialized. Authenticated:', isAuth);
      return isAuth;
    } catch (error) {
      console.error('Failed to initialize Keycloak:', error);
      setKeycloakInstance(keycloak);
      resolveKeycloakReady(keycloak);
      return false;
    }
  })();

  return initPromise;
}

function KeycloakProvider({ children }) {
  const [authenticated, setAuthenticated] = useState(false);
  const [initialized, setInitState] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getInitPromise().then((isAuth) => {
      if (cancelled) return;
      setAuthenticated(isAuth);
      setInitState(true);
    });
    return () => { cancelled = true; };
  }, []);

  if (!initialized) {
    return <LoadingComponent />;
  }

  return (
    <KeycloakContext.Provider value={{ keycloak, authenticated, initialized }}>
      {children}
    </KeycloakContext.Provider>
  );
}

export default KeycloakProvider;
